import { TypePill, type TypePillProps } from '@wundo/ui';
import type { MeetingKind } from '@wundo/shared/calendar.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What kind of conversation a meeting is, as the pill every overview wears.
 *
 * The kind and never the gesprekstype on top of it. An agenda is read over
 * shoulders, projected in meeting rooms and left open on a desk; a card that
 * says "Disfunctionering" has told the room what the conversation is about
 * before it happened. A 1:1 reads "1:1" whichever structure it follows, and
 * the gesprekstype stays on the meeting's own page.
 */
const VARIANT_BY_KIND: Record<MeetingKind, TypePillProps['type']> = {
  one_on_one: 'oneOnOne',
  /*
   * Neutral, both of them. Colour here would only be decoration — it is the
   * navy 1:1 that has to stand out on a calendar full of group meetings.
   */
  team: 'none',
  other: 'none',
};

/**
 * What a meeting is called, when it is one of the two Wundo can name. A meeting
 * that is neither is not labelled at all — see below.
 */
const LABEL_BY_KIND: Partial<Record<MeetingKind, () => string>> = {
  one_on_one: m.meeting_kind_one_on_one,
  team: m.meeting_kind_team,
};

export interface MeetingKindPillProps {
  kind: MeetingKind;
}

/**
 * A meeting that is neither a 1:1 nor a team meeting wears nothing.
 *
 * Not an empty pill and not the words "geen type": what kind of conversation
 * this is has no answer here, and printing that it has none says less than
 * saying nothing while taking the same room on the card.
 */
export function MeetingKindPill({ kind }: MeetingKindPillProps) {
  const label = LABEL_BY_KIND[kind];
  if (!label) return null;

  return (
    <TypePill type={VARIANT_BY_KIND[kind]} size='sm'>
      {label()}
    </TypePill>
  );
}
